import {
  FabricationPurchaseOrderState,
  PurchaseItemType,
  PurchaseOrderBadgeView,
  PurchaseOrderItemSource
} from '../../../api/dealer-api-interface-all';
import { goURL, resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { html } from 'lit';
import { LangFabricationPurchaseOrder } from '../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { resourceFabricationPurchaseOrder } from '../../../fabrication-purchase-orders/ui/launcher';

export function getPurchaseOrderStateLabel(state: FabricationPurchaseOrderState): string {
  switch (state) {
    case FabricationPurchaseOrderState.Draft:
      return LangFabricationPurchaseOrder.draftTab();
    case FabricationPurchaseOrderState.Issued:
      return LangFabricationPurchaseOrder.issuedTab();
    case FabricationPurchaseOrderState.IssuedPending:
      return LangFabricationPurchaseOrder.issuedPendingState();
    case FabricationPurchaseOrderState.Completed:
      return LangFabricationPurchaseOrder.completedTab();
    case FabricationPurchaseOrderState.Cancelled:
      return LangFabricationPurchaseOrder.cancelledTab();
    default:
      return '';
  }
}

export function getPurchaseOrderStateCssName(state: FabricationPurchaseOrderState): string {
  return FabricationPurchaseOrderState[state].toLowerCase();
}

export function getPurchaseOrderStatusBadge(state: FabricationPurchaseOrderState) {
  return html`<span class="badge badge-${getPurchaseOrderStateCssName(state)}"
    >${getPurchaseOrderStateLabel(state)}</span
  >`;
}

export function getPurchaseOrderNumberFormatted(po: { purchaseOrderNumber: number }): string {
  return `FPO-${po.purchaseOrderNumber}`;
}

// Why intercept the click? A bare href does a full-page reload; the SPA router only
// navigates client-side when the left-click is preventDefault'd and routed via goURL.
// The href is kept for accessibility / ctrl+click / middle-click (modifier keys fall through).
export function spaNavigate(e: MouseEvent, navigate: () => void) {
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
  e.preventDefault();
  navigate();
}

/** A clickable link to a PO's detail page that routes client-side (SPA), not a full reload. */
export function getPurchaseOrderLink(po: { id: string }, label: string) {
  return html`<a
    class="fabrication-purchase-order-link"
    href="${resolveURL(resourceFabricationPurchaseOrder, po.id)}"
    @click=${(e: MouseEvent) => spaNavigate(e, () => goURL(resourceFabricationPurchaseOrder, po.id))}
    >${label}</a
  >`;
}

/** A clickable badge linking to a PO's detail page; colour reflects the PO state. Used to show a job's PO membership. */
export function getPurchaseOrderBadgeLink(po: PurchaseOrderBadgeView) {
  return html`<a
    class="po-badge-link"
    href="${resolveURL(resourceFabricationPurchaseOrder, po.id)}"
    @click=${(e: MouseEvent) => {
      e.stopPropagation();
      spaNavigate(e, () => goURL(resourceFabricationPurchaseOrder, po.id));
    }}
    ><span class="badge badge-${getPurchaseOrderStateCssName(po.state)}"
      >${getPurchaseOrderNumberFormatted(po)}</span
    ></a
  >`;
}

/**
 * The "Add To" badge that puts a job on a purchase order, styled to sit beside getPurchaseOrderBadgeLink.
 * Like that badge, the colour is on an inner span rather than the anchor: inside a table, the shadow
 * stylesheet colours in-cell links with `.wm-table-cell a`, which would out-specify `.badge-primary` on
 * the same element. It is an anchor rather than a button for the same reason - it has to style exactly
 * like the badges beside it.
 */
export function getAddToPurchaseOrderBadge(onAdd: () => void) {
  // eslint-disable-next-line lit-a11y/anchor-is-valid
  return html`<a
    class="po-badge-link"
    href="javascript:void(0)"
    role="button"
    title=${LangFabricationPurchaseOrder.addToPoMenu()}
    @click=${(e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      onAdd();
    }}
    ><span class="badge badge-primary">${LangFabricationPurchaseOrder.addToPoBadge()}</span></a
  >`;
}

export function getMaterialTypeLabel(t: PurchaseItemType): string {
  switch (t) {
    case PurchaseItemType.Extrusion:
      return LangFabricationPurchaseOrder.materialTypeExtrusion();
    case PurchaseItemType.Component:
      return LangFabricationPurchaseOrder.materialTypeComponent();
    case PurchaseItemType.Gasket:
      return LangFabricationPurchaseOrder.materialTypeGasket();
    case PurchaseItemType.Fill:
      return LangFabricationPurchaseOrder.materialTypeFill();
    default:
      return '';
  }
}

export function getSourceLabel(s: PurchaseOrderItemSource): string {
  switch (s) {
    case PurchaseOrderItemSource.Supplier:
      return LangFabricationPurchaseOrder.sourceSupplier();
    case PurchaseOrderItemSource.External:
      return LangFabricationPurchaseOrder.sourceExternal();
    default:
      return '';
  }
}
